/**
 * The cancel box's words and choices — DESIGN-part-cancel.md (Rob, 23/10/2026, layout A of
 * `Claude outputs/part-cancel-mock-v1.html`). Pure: `cancelLeave.checks.mjs` pins each rule.
 *
 * The box asks the server twice: GET /leave/requests/{id}/days (the days, which may be
 * given back, who will be told) and POST /give-back with `preview: true` (what the booking
 * becomes and how much goes back). ⚠️ THE SUMMARY IS THE SERVER'S PREVIEW, NEVER WORKED
 * OUT HERE — it is the same plan the real change then follows, so the box can't promise
 * one thing and do another. This file only chooses the words around it.
 *
 * The rules:
 * 1. Two choices on a booking of more than one day: the whole booking, or some of the days.
 * 2. 3A: when the person can't cancel the whole booking because some of it has been taken,
 *    the first choice becomes "Cancel the rest of this booking" (every day from today); when
 *    all of it has been taken there is nothing to choose, only the reason.
 * 3. The button says what it will do: "Cancel the whole booking", "Cancel the rest of this
 *    booking", or "Give back 1 day" / "Give back 7h 30m" from the preview.
 * 4. Nobody is named twice, and an empty list says nothing at all.
 */

export const WHOLE = 'whole';
export const REST = 'rest';
export const SOME = 'some';

/** "Jessica Pearson will be told." / "A and B will be told." / "" for nobody. */
export function toldSentence(names) {
  const list = [...new Set((names || []).filter(Boolean))];
  if (list.length === 0) return '';
  if (list.length === 1) return `${list[0]} will be told.`;
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]} will be told.`;
}

/** "Your booking" for your own leave, "Harvey Specter's booking" for somebody else's. */
export function whoseBooking(info) {
  if (!info || info.is_own) return 'Your booking';
  return `${info.employee_name}'s booking`;
}

/** The balance the time goes back on: "your balance" / "Harvey Specter's balance". */
export function whoseBalance(info) {
  if (!info || info.is_own) return 'your balance';
  return `${info.employee_name}'s balance`;
}

/** The days this person may tick. */
export function givableDays(info) {
  return ((info && info.days) || []).filter((d) => !d.blocked).map((d) => d.date);
}

/**
 * The first of the two choices (rule 2): {kind, title, detail, days} — or kind null when the
 * whole booking has been taken and nothing at all can be given back.
 */
export function firstChoice(info) {
  const all = ((info && info.days) || []).length;
  const days = givableDays(info);
  if (info && info.whole_blocked) {
    if (days.length === 0) {
      return { kind: null, title: '', detail: info.whole_blocked, days: [] };
    }
    return {
      kind: REST,
      title: 'Cancel the rest of this booking',
      detail: `Every day from today goes back on ${whoseBalance(info)}. Days already taken stay.`,
      days,
    };
  }
  const n = all === 1 ? '1 day' : `all ${all} days`;
  const deducted = !info || info.deducted !== false;
  return {
    kind: WHOLE,
    title: 'Cancel the whole booking',
    detail: deducted ? `${n[0].toUpperCase()}${n.slice(1)} ${all === 1 ? 'goes' : 'go'} back on ${whoseBalance(info)}.`
      : 'The whole booking is taken off.',
    days: ((info && info.days) || []).map((d) => d.date),
  };
}

/** Rule 3: what the red button says. */
export function actionLabel({ choice, preview, ticked }) {
  if (choice === WHOLE || (preview && preview.whole)) return 'Cancel the whole booking';
  if (choice === REST) return 'Cancel the rest of this booking';
  if (!ticked || ticked.length === 0) return 'Give back days';
  if (preview && preview.given_back_length) return `Give back ${preview.given_back_length}`;
  return ticked.length === 1 ? 'Give back 1 day' : `Give back ${ticked.length} days`;
}

/** "It stays approved." / "Both stay approved." / "It's still waiting for approval." */
export function statusLine(info, preview) {
  const parts = (preview && preview.parts) || [];
  const many = parts.length === 2 ? 'Both' : 'They all';
  if (info && info.status === 'pending') {
    return parts.length > 1 ? `${many} are still waiting for approval.` : "It's still waiting for approval.";
  }
  return parts.length > 1 ? `${many} stay approved.` : 'It stays approved.';
}
